import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { LoadingButton } from "~/components/loading-button";
import { Link as RouterLink } from "react-router-dom";
import { RemediationWithMondooCard } from "./components/RemediationEffortCard/RemediationWithMondooCard";
import { motion } from "motion/react";
import Particle from "./components/Dialog/Particle";
import { RefObject } from "react";
import { EffortStats } from "./types";
import { ANIMATION_DELAYS, ANIMATION_DURATIONS } from "../../animation";
import { RemediationEffortCard } from "./components/RemediationEffortCard/RemediationEffortCard";
import { SECURITY_PIPELINE_DISPLAY_NAME } from "../../constants";

interface CreatePullRequestStepTwoProps {
  onClose: () => void;
  workflowUrl: string;
  manualEffort: EffortStats;
  mondooEffort: EffortStats;
  manualAndMondooDiffString: string;
  onReplay: () => void;
  showParticles: boolean;
  animationKey: number;
  numberRef: RefObject<HTMLDivElement | null>;
  numberRect: DOMRect | null;
  modalRect: DOMRect | null;
}

export const CreatePullRequestStepTwo = ({
  onClose,
  workflowUrl,
  manualEffort,
  mondooEffort,
  onReplay,
  animationKey,
  numberRef,
  showParticles,
  numberRect,
  modalRect,
}: CreatePullRequestStepTwoProps) => {
  return (
    <motion.div key={animationKey}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATIONS.header, ease: "easeOut" }}
      >
        <DialogTitle
          variant="h5"
          sx={{
            alignSelf: "stretch",
            textAlign: "center",
            color: "text.light",
            fontWeight: 700,
            textTransform: "uppercase",
            p: 0,
            m: 0,
          }}
        >
          Fix Submitted
        </DialogTitle>
      </motion.div>
      <DialogContent sx={{ p: 0, m: 0, overflow: "visible" }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: ANIMATION_DURATIONS.header, ease: "easeOut" }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, textAlign: "center" }}
          >
            {SECURITY_PIPELINE_DISPLAY_NAME} created your pull request, and it's
            ready for review.
          </Typography>
        </motion.div>
        <Grid size={12} container spacing={2} sx={{ px: 6 }}>
          <Grid size={6}>
            <RemediationEffortCard
              manualEffort={manualEffort}
              animationKey={animationKey}
            />
          </Grid>
          <Grid size={6}>
            <RemediationWithMondooCard
              mondooEffort={mondooEffort}
              manualEffort={manualEffort}
              onReplay={onReplay}
              showParticles={showParticles}
              animationKey={animationKey}
              numberRef={numberRef}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "right",
          display: "flex",
          p: 0,
          pt: 3,
        }}
      >
        <Box
          component={motion.div}
          key={`buttons-${animationKey}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: ANIMATION_DELAYS.buttons }}
          sx={{ display: "flex", gap: 1 }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={onClose}
            component={RouterLink}
            to={workflowUrl}
          >
            View Workflow
          </Button>

          <LoadingButton
            variant="contained"
            color="primary"
            type="submit"
            buttonText="DONE"
            onClick={onClose}
            loading={false}
          />

          {/* Particles */}
          {showParticles && numberRect && modalRect && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                pointerEvents: "none",
                overflow: "hidden",
              }}
            >
              {Array.from({ length: 16 }, (_, i) => (
                <Particle
                  key={`particle-${i}-${animationKey}`}
                  index={i}
                  numberRect={numberRect}
                  modalRect={modalRect}
                />
              ))}
            </Box>
          )}
        </Box>
      </DialogActions>
    </motion.div>
  );
};
